import {
  ClockCircleOutlined,
  FacebookOutlined,
  InstagramOutlined,
} from '@ant-design/icons';
import { ShareIcon } from '@heroicons/react/outline';
import { Button, Dropdown, MenuProps } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { chapterPlaceholder } from 'assets';
import WhatsAppIcon from 'assets/CourseIcon/WhatsAppIcon';
import CopyClipBoard from 'assets/Icons/CopyClipBoard';
import ImportantIcon from 'assets/Icons/ImportantIcon';
import LineSvgIcon from 'assets/Icons/LineSvgIcon';
import LinkedInIcon from 'assets/Icons/LinkedInIcon';
import NodeSideIcon from 'assets/Icons/NodeSideIcon';
import NoteIcon from 'assets/Icons/NoteIcon';
import QuoteIcon from 'assets/Icons/QuoteIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sticky as _Sticky,
  StickyContainer as _StickyContainer,
} from 'react-sticky';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import CategoryFaqSection from '@/components/CategoryComp/CategoryFaqSection';
import { LikeComponent } from '@/components/ResourcesPage/ResourcesPage/ChaptersPage';
import AuthorHoverCard from '@/components/ResourcesPage/SchoolsPage/AuthorHoverCard';
import TableOfContents from '@/components/ResourcesPage/TableOfContents';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { authModalControl } from '@/atoms/authModalControl';
import { user } from '@/atoms/user';
import { SchoolState } from '@/pages/schools/[schoolId]/[postId]';
import { updateLike } from '@/services/School';
import { User } from '@/utils/interfaces/User';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

const StickyContainer: any = _StickyContainer;
const Sticky: any = _Sticky;

export type AddableType =
  | 'title'
  | 'paragraph'
  | 'image'
  | 'html'
  | 'key-takeaways'
  | 'important'
  | 'note'
  | 'need-to-know'
  | 'testimonial'
  | 'advertisement'
  | 'quote'
  | 'faq';

export interface ContentBlock {
  id: string;
  type: AddableType;
  value?: string;
  metadata?: {
    color?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    imageUrl?: string;
    altText?: string;
    items?: string[];
    extraValue?: any;
  };
}

export interface CustomFormData {
  title: string;
  thumbnail: any;
  blocks: ContentBlock[];
}

interface LivePreviewProps {
  formData: CustomFormData;
  loggedIn: boolean;
  data?: any;
  schoolData?: SchoolState;
  setImageBlockOffset: (offset: number | null) => void;
  imageBlockOffset: number | null;
}

interface TOCItem {
  id: string;
  title: string;
  type: string;
  level: number;
  hasChildren?: boolean;
}

const SchoolRender = ({
  formData,
  loggedIn,
  data,
  schoolData,
  setImageBlockOffset,
}: LivePreviewProps) => {
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  const [isActive, setIsActive] = useState(false);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const [isCopied, setIsCopied] = useState(false);
  const [, setVisible] = useRecoilState(authModalControl);
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const screens = useBreakpoint();
  const { post_id, school_id } = router.query;
  const editorRef = useRef<any>();
  const { CKEditor, ClassicEditor } = editorRef.current || {};
  const containerRef = useRef<HTMLDivElement>(null);

  const imageBlockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (imageBlockRef.current) {
      const offsetTop =
        imageBlockRef.current.getBoundingClientRect().top + window.scrollY;
      setImageBlockOffset(offsetTop);
    }
  }, [formData?.thumbnail]);

  useEffect(() => {
    editorRef.current = {
      CKEditor: require('@ckeditor/ckeditor5-react'),
      ClassicEditor: require('@ckeditor/ckeditor5-build-classic'),
    };
    setEditorLoaded(true);
  }, []);

  useEffect(() => {
    const items: TOCItem[] = [];

    formData?.blocks?.forEach(
      (block: {
        id: string;
        type: string;
        value?: string;
        metadata?: { items?: string[] };
      }) => {
        if (block.type === 'title' && (block.value ?? '').trim()) {
          const headingItem: TOCItem = {
            id: `title-${block.id}`,
            title: block.value ?? '',
            type: 'title',
            level: 1,
          };
          items.push(headingItem);
        }
      },
    );

    setTocItems(items);
  }, [formData]);

  const scrollToSection = (id: string, threshold = 100) => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const element = document.getElementById(id);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const targetY = rect.top + scrollTop - threshold;

    programmaticScrollTo({
      top: targetY,
      behavior: 'smooth',
    });

    setActiveId(id);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-40% 0px -40% 0px',
        threshold: 0.1,
      },
    );

    tocItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [tocItems]);

  const preprocessHTML = (html: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    const listItems = tempElement.querySelectorAll('li');

    listItems.forEach((li) => {
      for (let i = 0; i < li.childNodes.length; i++) {
        const node = li.childNodes[i];
        if (node instanceof HTMLElement && node.tagName === 'P') {
          while (node.firstChild) {
            li.appendChild(node.firstChild);
          }
          node.parentNode?.removeChild(node);
          i--;
        }
      }
    });

    return tempElement.innerHTML;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' && data?.previous_post) {
      router.push(`/schools/${data.previous_post}/`);
    } else if (event.key === 'ArrowRight' && data?.next_post) {
      router.push(`/schools/${data.next_post.slug}/`);
    }
  };

  const shareOnPlatform = (platform: string, e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = encodeURIComponent(
      `${'https://zebralearn.com/dl/'}${router.asPath}`,
    );
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    let shareUrl = '';

    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}`;
        break;
      case 'whatsapp':
        shareUrl = isMobile
          ? `whatsapp://send?text=${url}`
          : `https://web.whatsapp.com/send?text=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'instagram':
        alert('Instagram sharing is only available via the mobile app.');
        return;
      default:
        return;
    }

    window.open(shareUrl, '_blank');
  };

  const handleCopyLink = () => {
    const linkToCopy = `${'https://zebralearn.com/'}${router.asPath}`;

    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      })
      .catch((error) => {
        console.error('Failed to copy text:', error);
      });
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div
          className='flex gap-x-3 items-center cursor-pointer p-1 '
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyLink();
          }}
        >
          <motion.span
            initial={{ scale: 1 }}
            animate={{ scale: isCopied ? 1.2 : 1 }}
          >
            <CopyClipBoard />
          </motion.span>

          <motion.span initial={{ scale: 1 }} className='text-white'>
            {isCopied ? (
              'Copied!'
            ) : (
              <span className='text-white z-20'>Copy Link</span>
            )}
          </motion.span>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: '2',
      label: (
        <div
          onClick={(e) => shareOnPlatform('facebook', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <FacebookOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Facebook
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <div
          onClick={(e) => shareOnPlatform('instagram', e)}
          className='flex gap-x-3 items-center p-1'
        >
          <span>
            <InstagramOutlined className='text-xl translate-y-[2px]' />{' '}
          </span>
          Instagram
        </div>
      ),
    },
  ];

  const handleLikeClick = ({ state }: { state: string }) => {
    const eventParams: EventDetails = {
      event_name: state,
      source: 'schools',
      source_type: 'schools',
      source_id: data?.school_id?.toString() as string,
      source_name: data?.schoolName,
      sub_source: 'chapter',
      sub_source_id: post_id as string,
      sub_source_name: data?.title,
      unit: 'boolean',
      value: 1,
      meta: {
        click_source: 'school_reading_screen',
      },
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const handleLike = async ({
    likeable_type,
    likeable_id,
    liked,
  }: {
    likeable_type: 'post' | 'school';
    likeable_id: string;
    liked: boolean;
  }) => {
    try {
      await updateLike({
        likeable_type,
        likeable_id,
        liked,
      });
    } catch (error) {
      console.error('Error logging in with Google:', error);
    }
  };

  const toggleHeart = () => {
    const newState = !isActive;
    handleLikeClick({
      state: newState ? 'like' : 'unlike',
    });
    setIsActive(newState);
    setLikeCount((count) => (newState ? count + 1 : count - 1));

    handleLike({
      likeable_id: post_id as string,
      likeable_type: 'post',
      liked: newState,
    });
  };

  useEffect(() => {
    setLikeCount(data?.likeCount || 0);
    setIsActive(data?.liked || false);
  }, [schoolData]);

  const modifiedBlocks = useMemo(() => {
    if (!formData?.blocks) return [];

    if (screens.lg) {
      return formData.blocks;
    }
    const userId = uuidv4();
    const newAdBlock: ContentBlock = {
      id: userId,
      type: 'advertisement',
      value: 'value',
    };

    const result = [...formData.blocks];
    const insertAt = 3;

    if (result.length > insertAt) {
      result.splice(insertAt, 0, newAdBlock);
    } else {
      result.push(newAdBlock);
    }

    return result;
  }, [formData?.blocks, screens]);

  return (
    <div
      id='scroll-container'
      className='hide-scrollbar h-full md:pt-7 xl:pt-12 w-full'
    >
      <StickyContainer className='flex flex-col hide-scrollbar   md:flex-row relative w-full md:bg-black h-full md:rounded-xl'>
        <ShouldRender check={tocItems.length}>
          <div className=' relative z-10 md:min-w-[240px]'>
            <Sticky>
              {({
                style,
                isSticky,
              }: {
                style: React.CSSProperties;
                isSticky: boolean;
              }) => (
                <div
                  style={{
                    ...style,
                    paddingTop: isSticky
                      ? screens.md
                        ? '60px'
                        : '50px'
                      : '0px',
                  }}
                >
                  <TableOfContents
                    tocItems={tocItems}
                    activeId={activeId}
                    scrollToSection={scrollToSection}
                  />
                </div>
              )}
            </Sticky>
          </div>
        </ShouldRender>
        <div className=' w-full px-4 md:px-7 xl:px-12'>
          <div id='introduction' className='mb-8 w-full'>
            <h1 className='text-3xl lg:text-[40px] font-bold mt-2 mb-6 text-white leading-tight'>
              {formData.title}
            </h1>

            <div className='space-y-2 mb-6'>
              <div className='text-sm text-gray-400 lg:text-base'>
                Published By <span className='text-white pl-1'>ZebraLearn</span>
              </div>
              <ShouldRender check={schoolData?.authorData?.name}>
                <div className='text-sm text-gray-400 lg:text-base gap-x-2 flex'>
                  Reviewed by
                  <div className=''>
                    <AuthorHoverCard
                      description={schoolData?.authorData?.bio || 'Bio'}
                      image={`${process.env.NEXT_PUBLIC_STUDIO_PATH}/${schoolData?.authorData?.authorAvatar}`}
                      name={schoolData?.authorData?.name || 'Author Name'}
                      profileLink={`${process.env.NEXT_PUBLIC_APP_ENV === 'staging' ? 'https://staging.zebralearn.com' : 'https://zebralearn.com'}/author/${schoolData?.authorData?.user_name}`}
                    />
                  </div>
                </div>
              </ShouldRender>
            </div>

            <div className='flex flex-col gap-x-3 '>
              <div
                className={clsx(
                  data?.reading_time ? ' justify-between ' : ' justify-end ',
                  'flex border-t items-center py-3 border-cartBgColor font-medium',
                )}
              >
                <ShouldRender check={data?.reading_time}>
                  <div className='flex'>
                    <ClockCircleOutlined className='w-6 h-6' />
                    {`${Math.round((Number(data?.reading_time) || 0) / 60)} Minutes`}
                  </div>
                </ShouldRender>
                <div className='flex  z-0'>
                  <LikeComponent
                    likeCount={likeCount}
                    isActive={isActive}
                    toggleHeart={() => {
                      loggedIn
                        ? toggleHeart()
                        : setVisible((prevState) => ({
                            ...prevState,
                            login: true,
                            signUp: false,
                          }));
                    }}
                  />

                  <div className='flex pl-1 space-x-2 z-20'>
                    <button
                      style={{
                        background:
                          'linear-gradient(360deg, #252525 0%, #545454 100%)',
                      }}
                      className=' text-white pl-2 bg-opacity-70 pr-4  h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                      onClick={(e) => shareOnPlatform('linkedin', e)}
                    >
                      <span className='  flex justify-center gap-x-1.5 -translate-y-[1.5px] md:-translate-y-[1px]'>
                        <LinkedInIcon /> <p className='pt-[3px]'>share</p>
                      </span>
                    </button>

                    <button
                      className=' text-white p-1 bg-[#222326] bg-opacity-70 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                      onClick={(e) => shareOnPlatform('whatsapp', e)}
                    >
                      <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                        <WhatsAppIcon />
                      </span>
                    </button>
                    <Dropdown
                      menu={{ items }}
                      arrow={true}
                      placement='bottomLeft'
                    >
                      <button
                        className=' text-white p-1 bg-[#222326] bg-opacity-70 w-8 h-8 flex gap-x-2 items-center text-[10px] rounded-full'
                        onClick={(e) => shareOnPlatform('', e)}
                      >
                        <span className='  flex justify-center gap-x-1.5 -translate-y-[1px]'>
                          <ShareIcon
                            className='w-5 h-5 text-[#AAAAAA]'
                            style={{ strokeWidth: '1px' }}
                          />
                        </span>
                      </button>
                    </Dropdown>
                  </div>
                </div>
              </div>

              <div className='flex gap-1 text-base  mb-10 border border-y border-x-0 items-center py-3 z-0 border-cartBgColor  font-medium'>
                <span className='ml-auto text-xs z-10 text-textLightGray'>
                  Updated on {moment(data?.updated_at).format('D MMMM, YYYY')}
                </span>
              </div>
            </div>
            <ShouldRender check={formData.thumbnail}>
              <div
                ref={imageBlockRef}
                className='rounded-lg overflow-hidden relative'
              >
                <Image
                  src={formData?.thumbnail || chapterPlaceholder}
                  alt='Hero image showing cyclist in motion'
                  width={1200}
                  height={320}
                  className='w-full h-80 object-cover'
                />
              </div>
            </ShouldRender>

            <div className='flex flex-col w-full'>
              {modifiedBlocks?.map((block) => (
                <div key={block.id}>
                  {block.type === 'title' && block.value && (
                    <h2
                      id={`title-${block.id}`}
                      className='text-[22px] lg:text-[32px] font-bold scroll-mt-6 mt-8 lg:mt-10 line'
                      style={{
                        color: block.metadata?.color || '#ffffff',
                        fontWeight: block.metadata?.bold ? 'bold' : '600',
                        fontStyle: block.metadata?.italic ? 'italic' : 'normal',
                        textDecoration: block.metadata?.underline
                          ? 'underline'
                          : 'none',
                        lineHeight: '35px',
                        letterSpacing: '-0.22px',
                      }}
                    >
                      {block.value}
                    </h2>
                  )}

                  {block.type === 'paragraph' && block.value && (
                    <p
                      key={block.id}
                      className='mt-4 md:mt-5 '
                      dangerouslySetInnerHTML={{
                        __html: (block.value as string).replace(
                          /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>(.*?)<\/a>/g,
                          '<a href="$1" target="_blank" style="font-weight: 500;"$2>$3</a>',
                        ),
                      }}
                    ></p>
                  )}
                  {block.type === 'advertisement' && block.value && (
                    <div className='w-full h-full mt-8 md:mt-10 rounded-lg overflow-hidden relative flex-shrink-0'>
                      <div className='text-center text-sm text-[#9F9EA6] pb-3'>
                        Advertisement
                      </div>
                      <ShouldRender check={schoolData?.advertisementPath}>
                        <Link
                          href={schoolData?.advertisementUrl as string}
                          target='_blank'
                        >
                          <Image
                            alt='School Image'
                            src={`${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${schoolData?.advertisementPath}`}
                            width={1200}
                            height={800}
                            priority={true}
                          />
                        </Link>
                      </ShouldRender>
                    </div>
                  )}

                  {block.type === 'image' && block.metadata?.imageUrl && (
                    <div className='rounded-xl mt-8 lg:mt-10 w-full  relative'>
                      <Image
                        src={`${process.env.NEXT_PUBLIC_API_URL_IMAGE_BASE_URL_SCHOOLS}/${
                          block.metadata.imageUrl as string
                        }`}
                        alt='Uploaded preview'
                        width={1200}
                        height={800}
                        className='rounded-xl w-full h-auto object-cover'
                      />
                    </div>
                  )}

                  {block.type === 'html' && block.value && editorLoaded && (
                    <div
                      className='customCkeditor customCkeditorSchool border-0'
                      key={`CKEditor-${block.id}`}
                    >
                      <CKEditor
                        onReady={(editor: any) => {
                          editor.enableReadOnlyMode('static-view');
                        }}
                        disabled={true}
                        config={{
                          toolbar: [],
                          disabled: true,
                          readOnly: true,
                          allowedContent: true,
                          extraAllowedContent: 'p(*)[class]',
                          styles: [
                            {
                              name: 'Normal',
                              element: 'p',
                              attributes: { class: 'ck-normal-font' },
                            },
                            {
                              name: 'Large',
                              element: 'p',
                              attributes: { class: 'ck-large-font' },
                            },
                          ],
                          link: {
                            defaultTarget: '_blank',
                            decorators: {
                              openInNewTab: {
                                mode: 'manual',
                                label: 'Open in a new tab',
                                defaultValue: true,
                                attributes: {
                                  target: '_blank',
                                  rel: 'noopener noreferrer',
                                },
                              },
                            },
                          },
                        }}
                        className='customCkeditor customCkeditorSchool border-0'
                        data={preprocessHTML(block.value as string)}
                        editor={ClassicEditor}
                      />
                    </div>
                  )}

                  {block.type === 'key-takeaways' &&
                    block.metadata?.items &&
                    block.metadata.items.some((item) => item.trim()) && (
                      <div className='bg-black rounded-md p-5 lg:p-8 mt-8 lg:mt-10 relative'>
                        <div className='absolute left-0 top-0'>
                          <LineSvgIcon />
                        </div>
                        <h3 className='text-white font-medium text-lg lg:text-xl mb-5'>
                          Key Takeaways:
                        </h3>
                        <p className='mb-3 text-sm lg:text-base'>
                          {block.metadata.extraValue}
                        </p>
                        <ul className='list-disc list-outside pl-3 lg:pl-5 text-white leading-relaxed marker:text-white marker:text-sm lg:marker:text-base'>
                          {block.metadata.items
                            .filter((item) => item.trim())
                            .map((item, index) => (
                              <li
                                key={index}
                                className='text-sm lg:text-base mb-1'
                              >
                                {item}
                              </li>
                            ))}
                        </ul>
                        <div className='absolute right-0 bottom-0 rotate-180'>
                          <LineSvgIcon />
                        </div>
                      </div>
                    )}
                  {block.type === 'need-to-know' &&
                    block.metadata?.items &&
                    block.metadata.items.some((item) => item.trim()) && (
                      <div className=' flex flex-col items-center pb-8  mt-12 lg:mt-14 rounded-xl border-[1.5px] px-16 border-[#282829] bg-[#121212]'>
                        <div className='flex flex-col items-start gap-2.5 absolute left-[20%] -mt-8  py-0 px-8'>
                          <div className='flex flex-col items-start gap-5  py-[9px] lg:py-3 px-6 rounded-full border-[1.5px] border-[#282829] bg-black  text-white text-lg lg:text-xl font-semibold leading-7'>
                            Need to Know
                          </div>
                        </div>
                        <ul className='list-disc list-outside w-full mt-12 lg:mt-[52px] text-white'>
                          {block.metadata.items
                            .filter((item) => item.trim())
                            .map((item, index) => (
                              <li
                                key={index}
                                className='text-sm lg:text-base mb-1'
                              >
                                {item}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  {block.type === 'important' && block.value && (
                    <div className='bg-[#121212]  border-[#282829] border  mt-8 lg:mt-10 p-5 lg:p-8 rounded-xl  relative'>
                      <h3 className='text-white flex gap-x-3 font-semibold text-xl lg:text-2xl mb-5'>
                        <ImportantIcon /> Important
                      </h3>
                      <p className='text-white leading-relaxed text-sm lg:text-base'>
                        {block.value}
                      </p>
                    </div>
                  )}
                  {block.type === 'note' && block.value && (
                    <div
                      className='relative rounded-[12px] p-[1px] mt-8 lg:mt-10'
                      style={{
                        background: `linear-gradient(254.55deg, rgba(255, 255, 255, 0) 63.83%, rgba(255, 255, 255, 0.2) 98.1%)`,
                      }}
                    >
                      <div className='bg-[#121212] rounded-xl relative p-5 lg:p-8'>
                        <h3 className='text-white flex gap-x-3 font-semibold text-xl lg:text-2xl mb-4 lg:mb-5'>
                          <NoteIcon /> Note
                        </h3>
                        <p className='text-white leading-relaxed text-sm lg:text-base'>
                          {block.value}
                        </p>
                      </div>
                      <div className='absolute  -bottom-5 -right-5'>
                        <NodeSideIcon />
                      </div>
                    </div>
                  )}
                  {block.type === 'quote' && block.value && (
                    <div className=' flex flex-col mt-8 lg:mt-10 mb-4 items-center relative w-full'>
                      <div className='flex flex-col items-center gap-5  pt-8 pb-14 px-8 rounded-xl w-full text-center border-[1.5px] border-white  text-white text-lg lg:text-xl font-medium leading-7'>
                        {block.value}
                      </div>
                      <div className='flex flex-col items-center gap-2.5 absolute  -bottom-10 py-0 px-8'>
                        <QuoteIcon />
                      </div>
                    </div>
                  )}
                  {block.type === 'testimonial' && block.value && (
                    <div className='flex flex-col items-start gap-y-3  lg:gap-y-4  py-6 px-8 mt-8 lg:mt-10 border-l-[6px] border-l-[#c7c6cf] bg-[#121212]'>
                      <div className=' text-white text-sm lg:text-base  italic font-light leading-6'>
                        {block.value}
                      </div>
                      <div className=' text-white text-start text-sm lg:text-base font-medium leading-6'>
                        By ZebraLearn
                      </div>
                    </div>
                  )}
                  {block.type === 'faq' && block?.metadata?.extraValue && (
                    <div className='w-full flex mt-10'>
                      <CategoryFaqSection
                        faqData={block?.metadata?.extraValue}
                        parentClassName='px-4 md:px-6 w-full'
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className='flex pb-10 '
          >
            <div className='flex justify-between w-full mt-6 md:mt-10'>
              <div>
                <ShouldRender check={data?.previous_post}>
                  <div className='w-full'>
                    <Link
                      href={`/schools/${schoolData?.schoolSlug}/${data?.previous_post?.slug}/?school_id=${school_id}&post_id=${data?.previous_post?.postId}`}
                    >
                      <Button
                        type='text'
                        className='text-base md:text-xl p-0 font-medium bg-transparent  text-primary'
                      >
                        Previous Chapter
                      </Button>
                    </Link>
                  </div>
                </ShouldRender>
              </div>

              <ShouldRender check={data?.next_post}>
                <div className='w-full  flex justify-end'>
                  <Link
                    href={`/schools/${schoolData?.schoolSlug}/${data?.next_post?.slug}/?school_id=${school_id}&post_id=${data?.next_post?.postId}`}
                  >
                    <Button
                      type='text'
                      className='text-base md:text-xl bg-transparent p-0 font-medium text-primary'
                    >
                      Next Chapter
                    </Button>
                  </Link>
                </div>
              </ShouldRender>
            </div>
          </div>
        </div>
      </StickyContainer>
    </div>
  );
};

export default SchoolRender;
